import { useEffect, useCallback, useRef } from 'react';

export type KeyboardShortcutHandler = (event: KeyboardEvent) => void;

export interface KeyboardShortcut {
  key: string | string[];
  handler: KeyboardShortcutHandler;
  requireModifier?: boolean; // Default: true
  preventDefault?: boolean; // Default: true
  shiftKey?: boolean;
  altKey?: boolean;
}

/**
 * Function that determines if a keyboard event should be ignored
 * @param event - The keyboard event
 * @returns true if the event should be ignored, false otherwise
 */
export type ShouldIgnoreKeyboardEvent = (event: KeyboardEvent) => boolean;

/**
 * Default condition that checks if an input field is focused.
 * This is exported so it can be reused or customized.
 */
export const defaultShouldIgnoreKeyboardEvent: ShouldIgnoreKeyboardEvent = (
  _event: KeyboardEvent,
) => {
  const activeElement = document.activeElement;
  return (
    activeElement?.tagName === 'INPUT' ||
    activeElement?.tagName === 'TEXTAREA' ||
    activeElement?.getAttribute('contenteditable') === 'true' ||
    activeElement?.closest('[contenteditable="true"]') !== null
  );
};

export interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean; // Default: true
  /**
   * Custom function to determine if a keyboard event should be ignored.
   * If not provided, defaults to checking if an input/textarea/contenteditable is focused.
   * Return true to ignore the event, false to process it.
   */
  shouldIgnore?: ShouldIgnoreKeyboardEvent;
}

/**
 * Custom hook for managing keyboard shortcuts with automatic Mac/Windows detection
 *
 * @param options - Configuration for keyboard shortcuts
 * @returns void
 *
 * @remarks
 * The hook uses refs internally to store shortcuts and handlers, so you don't need
 * to memoize the shortcuts array. The event listener is only re-registered when
 * `enabled` changes, not on every render. This provides optimal performance.
 *
 * @example
 * Basic usage (uses default input field detection):
 * ```tsx
 * useKeyboardShortcuts({
 *   shortcuts: [
 *     {
 *       key: 'c',
 *       handler: () => handleCopy(),
 *     },
 *     {
 *       key: ['+', '='],
 *       handler: () => handleZoomIn(),
 *     },
 *   ],
 * });
 * ```
 *
 * @example
 * Custom condition to ignore events:
 * ```tsx
 * useKeyboardShortcuts({
 *   shortcuts: [
 *     { key: 'Enter', handler: () => handleSubmit() },
 *   ],
 *   shouldIgnore: (event) => {
 *     // Ignore when certain elements are focused or conditions are met
 *     const activeElement = document.activeElement;
 *     const isInputFocused = activeElement?.tagName === 'INPUT' ||
 *                           activeElement?.tagName === 'TEXTAREA';
 *     return isInputFocused || isTextareaHovered; // Custom condition
 *   },
 * });
 * ```
 */
export function useKeyboardShortcuts(options: UseKeyboardShortcutsOptions) {
  const {
    shortcuts,
    enabled = true,
    shouldIgnore = defaultShouldIgnoreKeyboardEvent,
  } = options;

  // Use refs to store the latest values so the event listener doesn't need to be
  // re-registered when shortcuts or handlers change. This prevents unnecessary
  // event listener cleanup and re-registration on every render.
  const shortcutsRef = useRef(shortcuts);
  const shouldIgnoreRef = useRef(shouldIgnore);

  // Update refs with latest values (refs are mutable, so this doesn't trigger re-renders)
  shortcutsRef.current = shortcuts;
  shouldIgnoreRef.current = shouldIgnore;

  // Detect if the platform is Mac
  const isMac = useCallback(() => {
    return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  }, []);

  // Check if the modifier key (Command on Mac, Control on Windows) is pressed
  const isModifierPressed = useCallback(
    (event: KeyboardEvent) => {
      return isMac() ? event.metaKey : event.ctrlKey;
    },
    [isMac],
  );

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if the custom condition indicates we should ignore this event
      if (shouldIgnoreRef.current(event)) {
        return;
      }

      // Process each shortcut using the ref to get the latest shortcuts
      for (const shortcut of shortcutsRef.current) {
        const keys = Array.isArray(shortcut.key)
          ? shortcut.key
          : [shortcut.key];
        const requireModifier = shortcut.requireModifier !== false; // Default true
        const shouldPreventDefault = shortcut.preventDefault !== false; // Default true

        // Check if the pressed key matches
        const keyMatches = keys.some((k) => event.key === k);

        if (!keyMatches) continue;

        // Check modifier key requirement
        if (requireModifier && !isModifierPressed(event)) {
          continue;
        }

        // Check optional shift key requirement
        if (
          shortcut.shiftKey !== undefined &&
          event.shiftKey !== shortcut.shiftKey
        ) {
          continue;
        }

        // Check optional alt key requirement
        if (shortcut.altKey !== undefined && event.altKey !== shortcut.altKey) {
          continue;
        }

        // All conditions met, trigger handler
        if (shouldPreventDefault) {
          event.preventDefault();
        }
        shortcut.handler(event);
        break; // Only trigger first matching shortcut
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, isModifierPressed]);
}

/**
 * Helper function to get the modifier key display string (⌘ on Mac, Ctrl on Windows)
 */
export function getModifierKeyDisplay(): string {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  return isMac ? '⌘' : 'Ctrl';
}

/**
 * Helper function to format a keyboard shortcut string for display
 *
 * @example
 * formatShortcut('c') // Returns "⌘ c" on Mac or "Ctrl c" on Windows
 * formatShortcut(['+', '=']) // Returns "⌘ +" or "Ctrl +"
 */
export function formatShortcut(
  key: string | string[],
  options?: {
    shiftKey?: boolean;
    altKey?: boolean;
  },
): string {
  const modifier = getModifierKeyDisplay();
  const keyDisplay = Array.isArray(key) ? key[0] : key;
  let shortcut = `${modifier} ${keyDisplay}`;

  if (options?.shiftKey) {
    shortcut = `${modifier} Shift ${keyDisplay}`;
  }
  if (options?.altKey) {
    shortcut = `${modifier} Alt ${keyDisplay}`;
  }

  return shortcut;
}
